import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { sidebarItems } from '../components/sidebarConfig';
import { getAllRoles, getUserPermissions } from '../api';
import { toast } from 'react-toastify';

// Crear el contexto
export const AuthContext = createContext();

// Hook personalizado para acceder al contexto
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolesConfig, setRolesConfig] = useState({});
  const [sidebarKey, setSidebarKey] = useState(0); 
  const [lastPermissionCheck, setLastPermissionCheck] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(false);

  // Cargar los permisos para un rol específico - usando un enfoque híbrido
  // Wrapping with useCallback to prevent infinite rendering loop
  const loadRolePermissions = useCallback(async (role) => {
    if (!role) return;

    console.log(`Cargando permisos para el rol: ${role}`);
    
    try {
      // Primero cargamos la configuración completa de roles desde el localStorage
      const cachedRolesConfig = localStorage.getItem('rolesConfig');
      let rolesMap = {};
      
      if (cachedRolesConfig) {
        try {
          rolesMap = JSON.parse(cachedRolesConfig);
          console.log("Usando configuración de roles en caché:", rolesMap);
        } catch (e) {
          console.error("Error al parsear la caché de roles:", e);
        }
      }
      
      // Si el rol actual está en la caché y no es el administrador, podemos usar esos permisos directamente
      if (rolesMap[role] && role !== 'admin') {
        setPermissions(rolesMap[role] || []);
        setRolesConfig(rolesMap);
        console.log(`Permisos para rol ${role} cargados desde caché:`, rolesMap[role]);
        return;
      }

      // Si no está en caché o es el administrador, hacemos la solicitud a la API
      // Pero solo si es el administrador para evitar errores 403 para otros usuarios
      if (role === 'admin') {
        console.log("Consultando API de roles para el administrador");
        const roles = await getAllRoles();
        
        if (roles && Array.isArray(roles)) {
          // Crear un mapa de roles y sus permisos
          const apiRolesMap = {};
          roles.forEach(r => {
            apiRolesMap[r.name] = r.permissions || [];
          });
          
          // Guardar en localStorage para futuros usos
          localStorage.setItem('rolesConfig', JSON.stringify(apiRolesMap));
          
          setRolesConfig(apiRolesMap);
          setPermissions(apiRolesMap[role] || []);
          console.log(`Permisos para rol ${role} cargados desde API:`, apiRolesMap[role]);
          return;
        }
      }

      // Si llegamos aquí, significa que necesitamos usar el fallback con sidebarConfig
      // para usuarios no admin o si la API falló
      console.log(`Usando fallback con sidebarConfig para rol: ${role}`);
      const roleSidebarItems = sidebarItems.filter(item => 
        item.roles && item.roles.includes(role)
      );
      
      const paths = roleSidebarItems.map(item => item.path.replace('/', ''));
      console.log(`Permisos basados en sidebarConfig para el rol ${role}:`, paths);
      
      // Si no había caché previa, creamos una con los permisos del sidebar
      if (!cachedRolesConfig) {
        const newRolesMap = {};
        sidebarItems.forEach(item => {
          if (item.roles && item.roles.length) {
            item.roles.forEach(r => {
              if (!newRolesMap[r]) newRolesMap[r] = [];
              const path = item.path.replace('/', '');
              if (!newRolesMap[r].includes(path)) {
                newRolesMap[r].push(path);
              }
            });
          }
        });
        
        localStorage.setItem('rolesConfig', JSON.stringify(newRolesMap));
        setRolesConfig(newRolesMap);
      }
      
      setPermissions(paths);
    } catch (error) {
      console.error('Error loading role permissions:', error);
      
      // En caso de error, usar fallback con sidebarConfig
      const roleSidebarItems = sidebarItems.filter(item => 
        item.roles && item.roles.includes(role)
      );
      
      const paths = roleSidebarItems.map(item => item.path.replace('/', ''));
      setPermissions(paths);
    } finally {
      setLastPermissionCheck(Date.now());
    }
  }, []);  // Empty dependency array as it doesn't depend on any state/props

  // Verificar autenticación y rol del usuario al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');
      
      if (token) {
        setIsAuthenticated(true);
        setUserRole(role || '');

        try {
          // Intentar obtener permisos directamente del backend primero (más preciso)
          const permissionsResponse = await getUserPermissions();
          
          if (permissionsResponse && permissionsResponse.permissions) {
            console.log("Permisos obtenidos del backend al iniciar:", permissionsResponse.permissions);
            setPermissions(permissionsResponse.permissions);
            setLastPermissionCheck(Date.now());
          } else {
            // Si falla, usar el método de carga de permisos existente
            await loadRolePermissions(role);
          }
        } catch (error) {
          console.error("Error obteniendo permisos al iniciar:", error);
          // Usar el método de carga de permisos como fallback
          await loadRolePermissions(role);
        }
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, [loadRolePermissions]); // Added loadRolePermissions as a dependency

  // Función para iniciar sesión
  const login = useCallback((token, role) => {
    localStorage.setItem('token', token);
    localStorage.setItem('role', role);
    
    setIsAuthenticated(true);
    setUserRole(role);
    
    // Cargar permisos para el rol
    loadRolePermissions(role);
  }, [loadRolePermissions]);  // Added loadRolePermissions as a dependency

  // Función para cerrar sesión
  const logout = useCallback(() => {
    localStorage.clear();
    setIsAuthenticated(false);
    setUserRole('');
    setPermissions([]);
  }, []);

  // Método mejorado para actualizar permisos utilizando la API, con control de solicitudes innecesarias
  const updatePermissionsConfig = useCallback(async () => {
    try {
      // Verificar si ha pasado suficiente tiempo desde la última actualización (5 minutos = 300000ms)
      const now = Date.now();
      const timeSinceLastCheck = now - lastPermissionCheck;
      if (timeSinceLastCheck < 300000) {
        //console.log("Permisos verificados recientemente. Usando caché:", new Date(lastPermissionCheck).toLocaleTimeString());
        return true; // Usar los permisos en caché si han pasado menos de 5 minutos
      }

      console.log("Actualizando permisos del usuario actual...");
      
      // Verificar si hay un token, si no, no hacer nada
      const token = localStorage.getItem('token');
      if (!token) {
        console.log("No hay token de autenticación, no se pueden actualizar permisos");
        return false;
      }
      
      // Obtener los permisos directamente del backend para el usuario actual
      const permissionsResponse = await getUserPermissions();
      
      if (permissionsResponse && permissionsResponse.permissions) {
        // Comparar los permisos actuales con los nuevos para verificar si hay cambios
        const currentPermissionsStr = JSON.stringify(permissions.sort());
        const newPermissionsStr = JSON.stringify(permissionsResponse.permissions.sort());
        
        if (currentPermissionsStr !== newPermissionsStr) {
          // Solo actualizar si hay cambios
          setPermissions(permissionsResponse.permissions);
          console.log("Permisos actualizados desde el backend:", permissionsResponse.permissions);
          
          // Forzar re-renderizado del sidebar solo si hubo cambios
          setSidebarKey(prevKey => prevKey + 1);
        } else {
          console.log("Los permisos no han cambiado desde la última verificación");
        }
        
        setLastPermissionCheck(now);
        return true;
      } else {
        // Fallback: Si la API no devuelve permisos válidos
        console.log("La API no devolvió permisos válidos, usando fallback");
        await loadRolePermissions(userRole);
        return false;
      }
    } catch (error) {
      console.error("Error actualizando permisos:", error);
      
      // En caso de error, intentar con la función antigua solo si han pasado más de 5 minutos
      const now = Date.now();
      if (now - lastPermissionCheck > 300000) {
        await loadRolePermissions(userRole);
      }
      return false;
    } finally {
      setLastPermissionCheck(Date.now());
    }
  }, [userRole, permissions, lastPermissionCheck, loadRolePermissions]);

  // Función para verificar si un usuario tiene acceso a una ruta específica
  const hasAccess = useCallback((path) => {
    // Si no está autenticado, no tiene acceso
    if (!isAuthenticated) return false;
    
    // Si está en modo de carga, permitir temporalmente
    if (loading) return true;
    
    // Admin tiene acceso a todo
    if (userRole === 'admin') return true;
    
    // Convertir la ruta a un formato comparable con los permisos
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    
    // Verificar si tiene permiso para esta ruta
    return permissions.includes(normalizedPath);
  }, [isAuthenticated, loading, userRole, permissions]);

  // Efecto para forzar actualización cuando cambie forceUpdate
  useEffect(() => {
    if (forceUpdate) {
      console.log("Forzando actualización de componentes que usan permisos");
    }
  }, [forceUpdate]);

  // Contexto que se va a proporcionar
  const value = {
    isAuthenticated,
    userRole,
    permissions,
    loading,
    login,
    logout,
    hasAccess,
    updatePermissionsConfig,
    sidebarKey,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;